/**
 * Forecasted-spendings helpers for the payments matrix — pure functions (unit
 * tested in `tests/unit/forecast.unit.spec.ts`), no React and no API imports.
 *
 * The API already returns the forecast months per subscription, so all the
 * client does is place them in the window, keep the "actual wins" rule honest
 * against what is really on screen, and turn the result into ₽ when the loaded
 * payments carry a rate. Nothing here ever invents an FX rate.
 */

import type { ForecastItem, PaymentEvent } from '../api/types';
import { monthKeyOf } from './matrix';

/** `2026-09` for a UTC now. */
export function currentMonthKey(now: Date = new Date()): string {
  return monthKeyOf(now);
}

/**
 * `true` when the window ends in the current month or later — the only case
 * where a forecast has something to say (and where the toggle defaults to on).
 */
export function windowReachesForecast(months: string[], now: Date = new Date()): boolean {
  const last = months.at(-1);
  if (!last) return false;
  return last >= currentMonthKey(now);
}

/** One forecast row as the matrix renders it: a pill per month, nothing else. */
export interface ForecastRow {
  item: ForecastItem;
  /** `YYYY-MM` -> the pill amount (the subscription's `renewalCost`). */
  cells: Map<string, number>;
  /** Sum of the kept months, in the subscription's own currency. */
  perCurrencyTotals: Record<string, number>;
}

/**
 * Places the forecast months into the window and drops every subscription-month
 * that already shows a real pill. The API applies the same rule, but the matrix
 * is the source of truth for what is actually on screen — a loaded payment that
 * reached the window through a different query must win here too, not just
 * server-side.
 */
export function buildForecastRows(
  items: ForecastItem[],
  months: string[],
  actualMonthsBySubscription: ReadonlyMap<string, ReadonlySet<string>> = new Map(),
): ForecastRow[] {
  const window = new Set(months);
  const rows: ForecastRow[] = [];

  for (const item of items) {
    const actual = actualMonthsBySubscription.get(item.subscriptionId);
    const cells = new Map<string, number>();
    for (const month of item.months) {
      if (!window.has(month)) continue;
      if (actual?.has(month)) continue;
      cells.set(month, item.amount);
    }
    if (cells.size === 0) continue;

    const kept = [...cells.keys()].length;
    rows.push({
      item,
      cells,
      perCurrencyTotals: { [item.currency]: round2(item.amount * kept) },
    });
  }

  return rows;
}

/** `subscriptionId` -> the months of that subscription's real pills in the window. */
export function actualMonthsBySubscription(events: PaymentEvent[], months: string[]): Map<string, Set<string>> {
  const window = new Set(months);
  const result = new Map<string, Set<string>>();
  for (const event of events) {
    const key = monthKeyOf(event.occurredAt);
    if (!window.has(key)) continue;
    const bucket = result.get(event.subscriptionId) ?? new Set<string>();
    bucket.add(key);
    result.set(event.subscriptionId, bucket);
  }
  return result;
}

export interface ForecastTotals {
  /** Forecast sum per currency, as the API computed it. */
  perCurrency: Record<string, number>;
  /**
   * The same sum in ₽ — only when every currency in the forecast has a
   * last-known rate (or is RUB itself). `null` otherwise: an invented rate
   * would be worse than no number.
   */
  rubTotal: number | null;
}

/**
 * The last known ₽ rate per currency, read off the already-loaded payments (the
 * `fxRate` of a leg in the paid currency). No feed exists anywhere in this
 * codebase, so the freshest observed rate is the most honest one available —
 * and it is flagged with `≈` wherever it is shown.
 */
export function lastKnownFxRates(events: PaymentEvent[]): Record<string, number> {
  const byCurrency = new Map<string, { rate: number; at: number }>();
  for (const event of events) {
    if (event.paidCurrency.toUpperCase() === 'RUB') continue;
    for (const leg of event.transactions) {
      if (leg.fxRate === null) continue;
      // The rate only describes the leg it was booked on: a conversion leg is
      // a different currency from the payment itself.
      if (leg.currency.toUpperCase() !== event.paidCurrency.toUpperCase()) continue;
      const at = new Date(event.occurredAt).getTime();
      const known = byCurrency.get(event.paidCurrency);
      if (!known || at >= known.at) byCurrency.set(event.paidCurrency, { rate: leg.fxRate, at });
    }
  }
  return Object.fromEntries([...byCurrency].map(([currency, { rate }]) => [currency, rate]));
}

/** `amount × rate` per currency, or `null` while any rate is missing. */
export function forecastTotals(
  perCurrency: Record<string, number>,
  rates: Record<string, number>,
): ForecastTotals {
  let rubTotal = 0;
  for (const [currency, amount] of Object.entries(perCurrency)) {
    if (currency.toUpperCase() === 'RUB') {
      rubTotal += amount;
      continue;
    }
    const rate = rates[currency];
    if (rate === undefined || rate <= 0) return { perCurrency, rubTotal: null };
    rubTotal += amount * rate;
  }
  return { perCurrency, rubTotal: round2(rubTotal) };
}

/**
 * ₽ for one forecast cell, from the same last-known rates — `null` when the
 * currency has no rate, in which case the cell simply shows no ₽.
 */
export function forecastRub(amount: number, currency: string, rates: Record<string, number>): number | null {
  if (currency.toUpperCase() === 'RUB') return round2(amount);
  const rate = rates[currency];
  if (rate === undefined || rate <= 0) return null;
  return round2(amount * rate);
}

/** The forecast cells of one subscription, keyed for the matrix rows. */
export function forecastBySubscription(rows: ForecastRow[]): Map<string, ForecastRow> {
  return new Map(rows.map((row) => [row.item.subscriptionId, row]));
}

/** The forecast sums per currency across these rows - the visible set, so what
 * the user switched off leaves the totals as well as the grid. */
export function forecastPerCurrency(rows: ForecastRow[]): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const row of rows) {
    for (const [currency, amount] of Object.entries(row.perCurrencyTotals)) {
      totals[currency] = round2((totals[currency] ?? 0) + amount);
    }
  }
  return totals;
}

/** ≈₽ for one forecast row (its kept months summed), or `null` without a rate. */
export function forecastRowRub(row: ForecastRow, rates: Record<string, number>): number | null {
  let total = 0;
  for (const [currency, amount] of Object.entries(row.perCurrencyTotals)) {
    const rub = forecastRub(amount, currency, rates);
    if (rub === null) return null;
    total += rub;
  }
  return round2(total);
}

/**
 * What the "incl. forecast" footer says for one month. Three states, because
 * "nothing is forecast here" and "we refuse to invent a rate" are different
 * messages: `none` renders the muted dot, `no-rate` renders a dash.
 */
export type MonthForecast =
  | { kind: 'none' }
  | { kind: 'no-rate' }
  | { kind: 'rub'; value: number };

export function forecastRubByMonth(
  rows: ForecastRow[],
  months: string[],
  rates: Record<string, number>,
): MonthForecast[] {
  return months.map((month) => {
    let total = 0;
    let hasForecast = false;
    for (const row of rows) {
      if (row.cells.get(month) === undefined) continue;
      hasForecast = true;
      const rub = forecastRub(row.item.amount, row.item.currency, rates);
      if (rub === null) return { kind: 'no-rate' };
      total += rub;
    }
    return hasForecast ? { kind: 'rub', value: round2(total) } : { kind: 'none' };
  });
}

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}
